import React, { useMemo, useState } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { calcIHT } from "@/lib/iht";
import { InputForm } from "@/components/InputForm";
import { ResultsPanel } from "@/components/ResultsPanel";
import { Breakdown } from "@/components/Breakdown";
import { Explainer } from "@/components/Explainer";
import { PrintReport } from "@/components/PrintReport";

const initial = {
  estateValue: 750000,
  debtsFuneral: 5000,
  homeToDescendants: true,
  homeValue: 400000,
  spouseNrbUnused: 0,
  spouseRnrbUnused: 0,
  charityLegacy: 0,
  gifts7y: 0,
  giftYears: 4,
};

function App() {
  const [v, setV] = useState(initial);
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const r = useMemo(() => calcIHT(v), [v]);

  return (
    <div className="App">
      <PrintReport r={r} v={v} />
      {/* Header */}
      <header className="border-b-2 border-foreground bg-secondary print:hidden">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <a
            href="https://www.jameps.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="jameps-logo-link"
            className="flex items-center gap-3 group"
          >
            <span className="grid place-items-center h-12 w-12 rounded-xl border-2 border-foreground bg-white brutal-shadow-sm overflow-hidden shrink-0">
              <img
                src="https://www.jameps.com/_webedit/cached-images/32-0-813-1571-8180-6772-678.png"
                alt="JAM Estate Planning Services logo"
                className="h-10 w-10 object-contain"
              />
            </span>
            <span className="leading-tight">
              <span className="block font-display font-bold text-lg sm:text-xl">IHT Quick Calculator</span>
              <span className="block text-xs font-bold text-foreground/70 group-hover:text-foreground transition-colors">
                by JAM Estate Planning Services
              </span>
            </span>
          </a>
          <a
            href="https://www.jameps.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-jameps-cta"
            className="hidden sm:flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full border-2 border-foreground bg-white brutal-shadow-sm hover:-translate-y-0.5 hover:-translate-x-0.5 hover:shadow-[6px_6px_0px_#1e2b24] transition-[transform,box-shadow] duration-150"
          >
            Visit JAM EPS <ArrowUpRight size={15} strokeWidth={2.5} />
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12 print:hidden">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-10 md:mb-12 max-w-3xl"
        >
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Will your estate owe{" "}
            <span className="bg-primary border-2 border-foreground rounded-lg px-2 inline-block -rotate-1">
              Inheritance Tax?
            </span>
          </h1>
          <p className="mt-4 text-base md:text-lg font-medium text-foreground/80">
            A quick, no-sign-up estimate for the UK 2026/27 tax year. Results update live as you
            type. Brought to you by{" "}
            <a
              href="https://www.jameps.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-jameps-link"
              className="font-bold underline underline-offset-4 decoration-2 hover:text-accent"
            >
              JAM Estate Planning Services
            </a>
            . Not tax advice.
          </p>
        </motion.div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start">
          <section className="md:col-span-7 rounded-2xl border-2 border-foreground bg-white p-6 md:p-8 brutal-shadow">
            <InputForm v={v} set={set} />
          </section>

          <aside className="md:col-span-5 md:sticky md:top-8 flex flex-col gap-8">
            <div className="rounded-2xl border-2 border-foreground bg-primary p-6 md:p-8 brutal-shadow">
              <ResultsPanel r={r} />
            </div>
            <Breakdown r={r} />
          </aside>
        </div>

        {/* Allowances explainer */}
        <Explainer />

        {/* JAM EPS CTA */}
        <section
          data-testid="jameps-cta"
          className="mt-14 rounded-2xl border-2 border-foreground bg-primary p-6 md:p-8 brutal-shadow flex flex-col md:flex-row md:items-center gap-5 md:gap-8"
        >
          <div className="flex-1">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
              Worried about your estate? JAM can help.
            </h2>
            <p className="mt-2 text-base font-medium text-foreground/80">
              JAM Estate Planning Services makes wills, LPAs and secure storage simple and friendly —
              often in under 45 minutes, from the comfort of home. Life planning made easy.
            </p>
            <p className="mt-3 text-sm font-bold">
              Call{" "}
              <a href="tel:+448000025486" data-testid="jameps-phone" className="underline underline-offset-4">
                0800 002 5486
              </a>{" "}
              · Email{" "}
              <a href="mailto:hello@jameps.com" data-testid="jameps-email" className="underline underline-offset-4">
                hello@jameps.com
              </a>
            </p>
          </div>
          <a
            href="https://www.jameps.com/online-wills/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="jameps-cta-btn"
            className="shrink-0 flex items-center justify-center gap-2 px-6 py-4 rounded-xl border-2 border-foreground bg-white font-bold text-lg brutal-shadow-sm hover:-translate-y-0.5 hover:-translate-x-0.5 hover:shadow-[8px_8px_0px_#1e2b24] active:translate-y-0.5 active:shadow-none transition-[transform,box-shadow] duration-150"
          >
            Write my will <ArrowUpRight size={20} strokeWidth={2.5} />
          </a>
        </section>

        {/* Footer / disclaimer */}
        <footer className="mt-14 rounded-2xl border-2 border-foreground bg-white p-6 brutal-shadow-sm">
          <p className="text-sm font-medium text-foreground/80">
            <span className="font-bold">For general information only — not tax advice.</span>{" "}
            Estimates use simplified 2026/27 thresholds (NRB £325k, RNRB up to £175k, 40%/36% rates,
            £2m taper). For complex situations or planning, consult a qualified adviser.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
            <a
              href="https://www.gov.uk/inheritance-tax"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="govuk-link"
              className="text-sm font-bold underline underline-offset-4 hover:text-accent"
            >
              HMRC IHT guidance →
            </a>
            <a
              href="https://www.jameps.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-jameps-link"
              className="text-sm font-bold underline underline-offset-4 hover:text-accent"
            >
              JAM Estate Planning Services →
            </a>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            A calculator by JAM Estate Planning Services Ltd (registered in England &amp; Wales, company
            no. 13583094). 167-169 Great Portland Street, 5th Floor, London, W1W 5PF.
          </p>
        </footer>
      </main>
    </div>
  );
}

export default App;
